import { useState } from 'react'
import { api, ErroApi } from '../lib/api'
import './novo-bloqueio.css'

/**
 * Fechar um horário — férias, almoço, manutenção de sala, feriado.
 *
 * ─── POR QUE ISTO FALTAVA E CUSTAVA CARO ──────────────────────────
 *
 * `POST /v1/bloqueios` existe e é testado desde a semana da agenda. A
 * tela nunca foi escrita, e a grade mostrava uma fita de "em
 * construção" no lugar.
 *
 * Sem ela, a clínica tinha duas saídas, ambas ruins:
 *
 *  · marcar um agendamento falso para segurar o horário — o que suja o
 *    no-show e a ocupação, exatamente o que `db/0007_agenda.sql:34`
 *    diz que não pode acontecer; ou
 *  · não fazer nada, e o sistema deixar marcar em cima de quem não
 *    está lá.
 *
 * ─── RECORRÊNCIA É N REQUISIÇÕES, NÃO UMA REGRA ───────────────────
 *
 * "Toda terça" vira N bloqueios independentes, criados aqui. Regra de
 * recorrência no banco é modelagem que não se desfaz depois — e a
 * primeira vez que alguém precisar cancelar UMA terça, a regra vira
 * exceção sobre exceção.
 */

const TIPOS = [
  { id: 'ALMOCO', rotulo: 'Almoço', dica: 'todo dia, no mesmo horário' },
  { id: 'FERIAS', rotulo: 'Férias', dica: 'período inteiro, um profissional' },
  { id: 'FERIADO', rotulo: 'Feriado', dica: 'a clínica toda, o dia todo' },
  { id: 'MANUTENCAO', rotulo: 'Manutenção', dica: 'a sala ou o equipamento' },
  { id: 'REUNIAO', rotulo: 'Reunião', dica: 'quem participa' },
  { id: 'OUTRO', rotulo: 'Outro', dica: '' },
]

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

type Alvo = { id: string; nome: string; cor?: string | null }

export function NovoBloqueio({ recursos, profissionais, diaInicial, aoFechar, aoCriar }: {
  recursos: Alvo[]
  profissionais: Alvo[]
  diaInicial: Date
  aoFechar(): void
  aoCriar(): Promise<void>
}) {
  const iso = diaInicial.toISOString().slice(0, 10)

  const [tipo, setTipo] = useState('ALMOCO')
  const [titulo, setTitulo] = useState('')
  const [de, setDe] = useState(iso)
  const [ate, setAte] = useState(iso)
  const [hIni, setHIni] = useState('12:00')
  const [hFim, setHFim] = useState('13:00')
  const [diaTodo, setDiaTodo] = useState(false)
  const [semana, setSemana] = useState<number[]>([])
  const [alvoTipo, setAlvoTipo] = useState<'PROFISSIONAL' | 'RECURSO'>('PROFISSIONAL')
  const [alvos, setAlvos] = useState<string[]>([])
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const lista = alvoTipo === 'PROFISSIONAL' ? profissionais : recursos
  const escolhido = TIPOS.find((t) => t.id === tipo)

  /** Os dias que serão fechados, já expandidos. */
  function diasAlvo(): string[] {
    const saida: string[] = []
    const d = new Date(`${de}T00:00:00`)
    const fim = new Date(`${ate}T00:00:00`)
    while (d <= fim) {
      if (semana.length === 0 || semana.includes(d.getDay())) {
        saida.push(d.toISOString().slice(0, 10))
      }
      d.setDate(d.getDate() + 1)
      if (saida.length > 366) break   // trava de sanidade
    }
    return saida
  }

  const quantos = diasAlvo().length
  const podeSalvar = titulo.trim().length > 0 && alvos.length > 0 && quantos > 0 && !salvando

  async function salvar() {
    setSalvando(true); setErro(null)
    const ini = diaTodo ? '00:00' : hIni
    const fin = diaTodo ? '23:59' : hFim
    try {
      /* Um POST por dia. Sequencial, não em paralelo: a constraint de
         sobreposição é do banco, e mandar 30 de uma vez transformaria
         um conflito legítimo em erro de corrida difícil de explicar. */
      for (const dia of diasAlvo()) {
        await api('/v1/bloqueios', {
          metodo: 'POST',
          corpo: {
            tipo, titulo: titulo.trim(),
            inicio: new Date(`${dia}T${ini}:00`).toISOString(),
            fim: new Date(`${dia}T${fin}:00`).toISOString(),
            alvos: alvos.map((id) => ({ alvo_tipo: alvoTipo, alvo_id: id })),
          },
        })
      }
      await aoCriar()
    } catch (e) {
      setErro(e instanceof ErroApi
        ? (e.eConflito
            ? 'Já existe atendimento marcado dentro desse período. Cancele ou remarque antes de fechar o horário.'
            : e.message)
        : 'Não foi possível fechar o horário.')
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel nbl" role="dialog" aria-modal="true"
             aria-label="Fechar um horário">
        <header className="painel__topo">
          <h2>Fechar um horário</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="nbl-tipo">Por quê</label>
          <select id="nbl-tipo" className="entrada" value={tipo}
                  onChange={(e) => {
                    setTipo(e.target.value)
                    const t = TIPOS.find((x) => x.id === e.target.value)
                    if (t && !titulo.trim()) setTitulo(t.rotulo)
                  }}>
            {TIPOS.map((t) => <option key={t.id} value={t.id}>{t.rotulo}</option>)}
          </select>
          {escolhido?.dica && <span className="ajuda">{escolhido.dica}</span>}
        </div>

        <div className="campo">
          <label htmlFor="nbl-titulo">O que aparece na grade</label>
          <input id="nbl-titulo" className="entrada" maxLength={120}
                 placeholder="Almoço, Férias da Ana, Manutenção do tanque…"
                 value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          <span className="ajuda">
            Quem olhar a agenda vê este texto. "Bloqueado" não diz nada a quem
            precisa remarcar.
          </span>
        </div>

        <fieldset className="nbl__quem">
          <legend>De quem</legend>
          <div className="nbl__abas">
            <button type="button"
                    className={alvoTipo === 'PROFISSIONAL' ? 'is-ativa' : ''}
                    onClick={() => { setAlvoTipo('PROFISSIONAL'); setAlvos([]) }}>
              Profissional
            </button>
            <button type="button"
                    className={alvoTipo === 'RECURSO' ? 'is-ativa' : ''}
                    onClick={() => { setAlvoTipo('RECURSO'); setAlvos([]) }}>
              Sala ou equipamento
            </button>
          </div>
          <div className="nbl__chips">
            {lista.map((a) => {
              const on = alvos.includes(a.id)
              return (
                <button key={a.id} type="button"
                        className={`nbl__chip${on ? ' is-ativa' : ''}`}
                        aria-pressed={on}
                        onClick={() => setAlvos(on
                          ? alvos.filter((x) => x !== a.id)
                          : [...alvos, a.id])}>
                  {a.nome}
                </button>
              )
            })}
            {lista.length === 0 && (
              <span className="ajuda">
                Nenhum cadastrado ainda. Ajustes › {alvoTipo === 'PROFISSIONAL'
                  ? 'Quem trabalha aqui' : 'Como a agenda funciona'}.
              </span>
            )}
          </div>
        </fieldset>

        <div className="nbl__datas">
          <div className="campo">
            <label htmlFor="nbl-de">De</label>
            <input id="nbl-de" className="entrada" type="date" value={de}
                   onChange={(e) => {
                     setDe(e.target.value)
                     if (e.target.value > ate) setAte(e.target.value)
                   }} />
          </div>
          <div className="campo">
            <label htmlFor="nbl-ate">Até</label>
            <input id="nbl-ate" className="entrada" type="date" value={ate} min={de}
                   onChange={(e) => setAte(e.target.value)} />
          </div>
        </div>

        {de !== ate && (
          <fieldset className="nbl__semana">
            <legend>Só nestes dias da semana</legend>
            <div className="nbl__chips">
              {DIAS.map((d, i) => {
                const on = semana.includes(i)
                return (
                  <button key={d} type="button"
                          className={`nbl__chip${on ? ' is-ativa' : ''}`}
                          aria-pressed={on}
                          onClick={() => setSemana(on
                            ? semana.filter((x) => x !== i)
                            : [...semana, i])}>
                    {d}
                  </button>
                )
              })}
            </div>
            <span className="ajuda">
              Nenhum marcado = todos os dias do período.
            </span>
          </fieldset>
        )}

        <label className="nbl__diatodo">
          <input type="checkbox" checked={diaTodo}
                 onChange={(e) => setDiaTodo(e.target.checked)} />
          <span>O dia inteiro</span>
        </label>

        {!diaTodo && (
          <div className="nbl__horas">
            <div className="campo">
              <label htmlFor="nbl-hi">Das</label>
              <input id="nbl-hi" className="entrada" type="time" value={hIni}
                     onChange={(e) => setHIni(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="nbl-hf">Às</label>
              <input id="nbl-hf" className="entrada" type="time" value={hFim}
                     onChange={(e) => setHFim(e.target.value)} />
            </div>
          </div>
        )}

        {/* O que vai acontecer, escrito antes de acontecer: 30 bloqueios
            criados sem aviso seriam 30 coisas para desfazer à mão. */}
        <p className="nbl__resumo ajuda">
          {quantos === 0
            ? 'Nenhum dia no período escolhido.'
            : quantos === 1
              ? `Fecha 1 dia${diaTodo ? ' inteiro' : `, das ${hIni} às ${hFim}`}.`
              : `Fecha ${quantos} dias${diaTodo ? ' inteiros' : `, das ${hIni} às ${hFim} em cada`}.`}
          {alvos.length > 1 && ` Para ${alvos.length} ${alvoTipo === 'PROFISSIONAL' ? 'profissionais' : 'salas'}.`}
        </p>

        <div className="nbl__acoes">
          <button className="botao botao--fantasma" onClick={aoFechar}>Cancelar</button>
          <button className="botao" disabled={!podeSalvar} onClick={() => void salvar()}>
            {salvando ? 'Fechando…' : 'Fechar o horário'}
          </button>
        </div>
      </aside>
    </>
  )
}
